import { useState, useEffect } from 'react';
import { HiEye, HiTrash, HiPhone, HiMail, HiLocationMarker, HiBookOpen } from 'react-icons/hi';
import { enquiriesService } from '../api/services';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import { TableSkeleton } from '../components/Skeleton';
import { sanitizeText, sanitizeErrorMessage } from '../utils/sanitize';
import { enquiryUpdateSchema, validateForm } from '../utils/validation';
import toast from 'react-hot-toast';

// Base URL without /api/v1 for serving files
const FILE_BASE = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    admin_notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await enquiriesService.getAll();
      setEnquiries(response.data);
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormData({
      status: enquiry.status,
      admin_notes: enquiry.admin_notes || '',
    });
    setErrors({});
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validation = validateForm(enquiryUpdateSchema, formData);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }

    setSubmitting(true);
    try {
      await enquiriesService.update(selectedEnquiry.id, formData);
      toast.success('Enquiry updated successfully');
      setShowModal(false);
      fetchEnquiries();
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (enquiry) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await enquiriesService.delete(enquiry.id);
        toast.success('Enquiry deleted');
        fetchEnquiries();
      } catch (error) {
        toast.error(sanitizeErrorMessage(error));
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${FILE_BASE}${path}`;
  };

  const columns = [
    { key: 'name', label: 'Name', render: (row) => sanitizeText(row.name) },
    { key: 'phone', label: 'Phone', render: (row) => sanitizeText(row.phone) },
    { key: 'email', label: 'Email', render: (row) => sanitizeText(row.email) },
    { key: 'state', label: 'State', render: (row) => sanitizeText(row.state) || '-' },
    {
      key: 'catalogue',
      label: 'Catalogue',
      render: (row) => row.catalogue ? (
        <span className="text-gold-500 font-medium">{sanitizeText(row.catalogue.name)}</span>
      ) : (
        <span className="text-gray-500">-</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleView(row); }}
            className="p-2 text-gray-400 hover:text-gold-500 transition-colors"
            aria-label="View enquiry"
          >
            <HiEye className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Delete enquiry"
          >
            <HiTrash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold gold-gradient-text">Enquiries</h1>
        <p className="text-gray-400 mt-1">Manage catalogue download & enquiries</p>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <TableSkeleton rows={5} columns={7} />
        ) : (
          <DataTable columns={columns} data={enquiries} onRowClick={handleView} />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Enquiry Details"
      >
        {selectedEnquiry && (
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-dark-500 rounded-lg">
                <div className="w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-dark-900 font-bold text-lg">
                    {sanitizeText(selectedEnquiry.name)?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-200 truncate">
                    {sanitizeText(selectedEnquiry.name)}
                  </p>
                  <p className="text-sm text-gray-500">{formatDate(selectedEnquiry.created_at)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <HiPhone className="w-5 h-5 text-gold-500 flex-shrink-0" />
                  <span className="truncate">{sanitizeText(selectedEnquiry.phone)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <HiMail className="w-5 h-5 text-gold-500 flex-shrink-0" />
                  <span className="truncate">{sanitizeText(selectedEnquiry.email)}</span>
                </div>
                {selectedEnquiry.state && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <HiLocationMarker className="w-5 h-5 text-gold-500 flex-shrink-0" />
                    <span className="truncate">{sanitizeText(selectedEnquiry.state)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Catalogue Info */}
            {selectedEnquiry.catalogue && (
              <div className="p-4 bg-dark-500 rounded-lg border border-gold-500/30">
                <div className="flex items-center gap-3">
                  {selectedEnquiry.catalogue.thumbnail_url ? (
                    <img
                      src={getFullUrl(selectedEnquiry.catalogue.thumbnail_url)}
                      alt={sanitizeText(selectedEnquiry.catalogue.name)}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-dark-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <HiBookOpen className="w-8 h-8 text-gold-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-400">Enquired Catalogue</p>
                    <p className="font-medium text-gold-500 truncate">
                      {sanitizeText(selectedEnquiry.catalogue.name)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Update Form */}
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={`input-field ${errors.status ? 'border-red-500' : ''}`}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="converted">Converted</option>
                  <option value="closed">Closed</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-400">{errors.status}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Admin Notes</label>
                <textarea
                  value={formData.admin_notes}
                  onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                  className={`input-field ${errors.admin_notes ? 'border-red-500' : ''}`}
                  rows={3}
                  placeholder="Add internal notes..."
                  maxLength={1000}
                />
                {errors.admin_notes && (
                  <p className="mt-1 text-sm text-red-400">{errors.admin_notes}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.admin_notes.length}/1000 characters
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center"
                  disabled={submitting}
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-dark-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Update Enquiry'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
}
